import styled, { css } from "styled-components"

import type {
  CSSCursor,
  CSSFlexAlignItems,
  CSSFlexJustifyContent,
  CSSTextTransform,
  CSSUserSelect,
  CSSWhiteSpace,
} from "../types/css"
import { ZIndexRange } from "../types/enums"
import type { BorderProps } from "../types/props"
import { getBorderStyle } from "../util/props/border"
import { overloadColor } from "../util/style"
import type { MenuSide } from "./DropdownMenu"

export const Wrapper = styled.div`
  position: relative;
  display: inline-block;
`

export interface DisplayStyle extends BorderProps {
  width: string
  height: string
  justifyContent: CSSFlexJustifyContent
  alignItems: CSSFlexAlignItems
  padding: string

  color: string
  background: string
  hoverColor: string
  hoverBackground: string

  fontSize: string
  textTransform: CSSTextTransform
  userSelect: CSSUserSelect
  cursor: CSSCursor
  borderRadius: string
}
export const Display = styled.div<DisplayStyle>`
  display: inline-flex;
  justify-content: ${(props) => props.justifyContent};
  align-items: ${(props) => props.alignItems};
  width: ${(props) => props.width};
  height: ${(props) => props.height};
  padding: ${(props) => props.padding};

  color: ${(props) => overloadColor(props.color)};
  background: ${(props) => overloadColor(props.background)};

  font-size: ${(props) => props.fontSize};
  text-transform: ${(props) => props.textTransform};
  user-select: ${(props) => props.userSelect};
  cursor: ${(props) => props.cursor};

  ${getBorderStyle}
  border-radius: ${(props) => props.borderRadius};

  &:hover {
    color: ${(props) => overloadColor(props.hoverColor)};
    background: ${(props) => overloadColor(props.hoverBackground)};
  }
`

type MenuItemProps = {}
export const MenuItem = styled.div<MenuItemProps>`
  position: relative;
`

interface CurtainSeparatorProps {
  height: string
}
export const CurtainSeparator = styled.div<CurtainSeparatorProps>`
  position: absolute;
  width: 100%;
  height: ${(props) => props.height};
`

export type CurtainType = "floating" | "integrated"
export interface CurtainStyle extends BorderProps {
  margin: string

  color: string
  background: string
  borderRadius: string
  shadow: string

  items: {
    padding: string

    hoverColor: string
    hoverBackground: string
    fontSize: string
    textTransform: CSSTextTransform
    userSelect: CSSUserSelect
    whiteSpace: CSSWhiteSpace
    cursor: CSSCursor
  }
}

function getMenuPosition(props: CurtainProps) {
  if (props.isSubMenu)
    return css`
      top: 0;
      ${props.side === "left" ? "left: 0" : "right: 0"};
      transform: translateX(${props.side === "left" ? "-100%" : "100%"});
    `

  switch (props.side) {
    case "top":
      return css`
        top: -${props.margin};
        transform: translateY(-100%);
      `
    case "bottom":
      return css`
        top: calc(100% + ${props.margin});
      `
    case "left":
      return css`
        top: 0;
        left: -${props.margin};
        transform: translateX(-100%);
      `
    case "right":
      return css`
        top: 0;
        left: calc(100% + ${props.margin});
      `
  }
}

export interface CurtainProps extends CurtainStyle {
  side: MenuSide
  isSubMenu: boolean
  subMenuLevel: number
  subMenuWidth: string
}
export const Curtain = styled.div<CurtainProps>`
  position: absolute;
  z-index: ${(props) => ZIndexRange.Dropdowns + props.subMenuLevel};

  width: ${(props) => props.subMenuWidth};
  min-width: ${(props) => (props.isSubMenu ? "0" : "100%")};

  color: ${(props) => overloadColor(props.color)};
  background: ${(props) => overloadColor(props.background)};

  ${getBorderStyle}
  border-radius: ${(props) => props.borderRadius};
  box-shadow: ${(props) => props.shadow};

  ${MenuItem} {
    padding: ${(props) => props.items.padding};

    font-size: ${(props) => props.items.fontSize};
    text-transform: ${(props) => props.items.textTransform};
    user-select: ${(props) => props.items.userSelect};
    white-space: ${(props) => props.items.whiteSpace};
    cursor: ${(props) => props.items.cursor};

    &:hover {
      color: ${(props) => overloadColor(props.items.hoverColor)};
      background: ${(props) => overloadColor(props.items.hoverBackground)};
    }

    &:first-of-type {
      border-top-left-radius: ${(props) => props.borderRadius};
      border-top-right-radius: ${(props) => props.borderRadius};
    }
    &:last-of-type {
      border-bottom-left-radius: ${(props) => props.borderRadius};
      border-bottom-right-radius: ${(props) => props.borderRadius};
    }
  }

  ${(props) => getMenuPosition(props)};
`
